// SideMenu.jsx - サイドメニューコンポーネント
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Settings,
  MapPin,
  CreditCard,
  ChevronRight,
  LogOut,
} from 'lucide-react';

const SideMenu = ({ isOpen, onClose, onNewSession, userProfile, onSignOut }) => {
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const go = (path) => () => {
    navigate(path);
    onClose?.();
  };

  const fullName = useMemo(() => {
    const last = userProfile?.last_name?.trim() || '';
    const first = userProfile?.first_name?.trim() || '';
    const joined = [last, first].filter(Boolean).join(' ');
    return joined || '—';
  }, [userProfile]);

  const genderText = useMemo(() => {
    return (userProfile?.gender || '').toString() || '—';
  }, [userProfile]);

  const ageText = useMemo(() => {
    const s = userProfile?.birthdate;
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const hasntHadBirthday =
      today.getMonth() < d.getMonth() ||
      (today.getMonth() === d.getMonth() && today.getDate() < d.getDate());
    if (hasntHadBirthday) age -= 1;
    return `${age}歳`;
  }, [userProfile]);

  return (
    <div
      className={`medichat-sidemenu ${
        isOpen ? 'medichat-sidemenu--open' : 'medichat-sidemenu--closed'
      }`}
      aria-hidden={!isOpen}
      inert={isOpen ? undefined : true}
    >
      <div className="medichat-sidemenu__header" style={{ flexDirection: 'column' }}>
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <button onClick={onClose} className="medichat-sidemenu__close-btn">
            閉じる
          </button>
        </div>
        <div className="medichat-sidemenu__profile">
          <div className="medichat-sidemenu__profile-meta">
            <div className="medichat-sidemenu__profile-meta">
              <div className="name">{fullName}</div>
              <div className="sub">
                {[
                  genderText || null,
                  ageText || null,
                  (userProfile?.prefecture || '').toString().trim() || null,
                ].filter(Boolean).join('・') || '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="medichat-sidemenu__content">
        <button onClick={onNewSession} className="medichat-new-session-btn">
          新しい診察を始める
        </button>

        <div className="medichat-sidemenu__list">
          <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/history')}>
            <span className="medichat-sidemenu__icon">
              <ClipboardList size={18} />
            </span>
            <span className="medichat-sidemenu__label">診療履歴</span>
            <span className="medichat-sidemenu__chev">
              <ChevronRight size={16} />
            </span>
          </button>

          <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/settings')}>
            <span className="medichat-sidemenu__icon">
              <Settings size={18} />
            </span>
            <span className="medichat-sidemenu__label">基本情報</span>
            <span className="medichat-sidemenu__chev">
              <ChevronRight size={16} />
            </span>
          </button>

          <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/insurance')}>
            <span className="medichat-sidemenu__icon"><CreditCard size={18} /></span>
            <span className="medichat-sidemenu__label">資格確認書・医療証</span>
            <span className="medichat-sidemenu__chev"><ChevronRight size={16} /></span>
          </button>

          <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/documents')}>
            <span className="medichat-sidemenu__icon"><ClipboardList size={18} /></span>
            <span className="medichat-sidemenu__label">書類管理</span>
            <span className="medichat-sidemenu__chev"><ChevronRight size={16} /></span>
          </button>

          <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/addresses')}>
            <span className="medichat-sidemenu__icon">
              <MapPin size={18} />
            </span>
            <span className="medichat-sidemenu__label">配送先住所</span>
            <span className="medichat-sidemenu__chev">
              <ChevronRight size={16} />
            </span>
          </button>

          <button className="medichat-sidemenu__item" onClick={go('/medichat/mypage/payments')}>
            <span className="medichat-sidemenu__icon">
              <CreditCard size={18} />
            </span>
            <span className="medichat-sidemenu__label">支払い方法登録</span>
            <span className="medichat-sidemenu__chev">
              <ChevronRight size={16} />
            </span>
          </button>
        </div>

        {/* 法的情報リンク */}
        <div className="medichat-sidemenu__legal">
          <div className="medichat-sidemenu__legal-row">
            <a href="https://www.notion.so/CureNow-2cba4be0fa7080cd8e3ee5b623afb4a7" target="_blank" rel="noreferrer">利用規約</a>
            <span className="medichat-sidemenu__legal-sep">|</span>
            <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080bc9233f81808fbb4d0" target="_blank" rel="noreferrer">プライバシーポリシー</a>
          </div>
          <div className="medichat-sidemenu__legal-row">
            <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa7080edb59ec9ef331e9ee9" target="_blank" rel="noreferrer">キャンセルポリシー</a>
            <span className="medichat-sidemenu__legal-sep">|</span>
            <a href="https://grey-brow-5dc.notion.site/CureNow-2cba4be0fa708045a472fab1f4069ba1" target="_blank" rel="noreferrer">特定商取引法に基づく表示</a>
          </div>
        </div>

        {/* ログアウト */}
        {onSignOut && (
          <div className="medichat-sidemenu__logout-wrap">
            {confirmLogout ? (
              <div className="medichat-sidemenu__logout-confirm">
                <p className="medichat-sidemenu__logout-confirm-text">ログアウトしますか？</p>
                <div className="medichat-sidemenu__logout-confirm-actions">
                  <button
                    className="medichat-sidemenu__logout-confirm-cancel"
                    onClick={() => setConfirmLogout(false)}
                    type="button"
                  >
                    キャンセル
                  </button>
                  <button
                    className="medichat-sidemenu__logout-confirm-ok"
                    onClick={() => { setConfirmLogout(false); onSignOut(); }}
                    type="button"
                  >
                    ログアウト
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="medichat-sidemenu__logout-btn"
                onClick={() => setConfirmLogout(true)}
                type="button"
              >
                <LogOut size={16} className="medichat-sidemenu__logout-icon" />
                ログアウト
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SideMenu;
